/*
 * pdf-seal.js — tamper-evidence seal for /sign-pdf and /verify-pdf.  window.PdfSeal
 *
 * WHAT THIS IS — and IS NOT
 * ─────────────────────────
 * This produces an *integrity seal*: proof that a PDF has not been altered
 * since the seal was created. It is NOT a legal electronic signature.
 * A legal e-signature (eIDAS / PAdES) needs a certificate issued by a trusted
 * authority and a trust chain — impossible to produce honestly in a browser.
 *
 * So the seal proves TAMPER-EVIDENCE, not IDENTITY: the signer name is
 * self-declared. /verify-pdf must say exactly that.
 *
 * HOW IT WORKS
 *   create:  hash = SHA-256(pdfBytes); ECDSA-P256 keypair; sig = sign(hash).
 *            Seal = { hash, sig, public key (JWK), signer, sealedAt }.
 *   verify:  recompute SHA-256(pdfBytes); if it differs from seal.hash the file
 *            was modified; else ECDSA-verify(sig) against the embedded pubkey.
 *
 * The seal is a SIDECAR (a small JSON / a base64 code), never embedded in the
 * PDF — embedding a hash of a file inside that same file is circular and would
 * depend on pdf-lib re-save determinism. A sidecar hashes the literal bytes the
 * recipient holds: zero circularity, fully honest.
 *
 * VERSION 2 (26.09.2026). Version 1 signed the 32-byte file hash ONLY: `signer`
 * and `sealedAt` sat next to it unprotected, so anyone could rewrite the name
 * or backdate the seal in the JSON and verification still said «valid» while
 * printing the forged name. v2 signs a canonical string of alg + hash + signer
 * + sealedAt (+ name/kind), so the metadata is covered by the signature.
 * v1 seals keep verifying (the file hash is still protected) and report
 * `metaProtected: false`, so a page can say the name and date are self-declared.
 */
(function (root) {
    'use strict';

    var SEAL_VERSION = 2;
    var ALG = 'ECDSA-P256-SHA256';

    function subtle() {
        var c = root.crypto || (typeof crypto !== 'undefined' ? crypto : null);
        if (!c || !c.subtle) throw new Error('webcrypto_unavailable');
        return c.subtle;
    }

    /* ── base64 <-> bytes / string ───────────────────────────────── */
    function bytesToB64(bytes) {
        var bin = '';
        for (var i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
        return btoa(bin);
    }
    function b64ToBytes(b64) {
        var bin = atob(b64);
        var out = new Uint8Array(bin.length);
        for (var i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
        return out;
    }
    function strToB64(str) {
        return bytesToB64(new TextEncoder().encode(str));
    }
    function b64ToStr(b64) {
        return new TextDecoder().decode(b64ToBytes(b64));
    }

    function sha256(bytes) {
        return subtle().digest('SHA-256', bytes).then(function (h) {
            return new Uint8Array(h);
        });
    }

    /* Canonical bytes a v2 seal signs. Key order is fixed by construction, so
       verify() rebuilds exactly these bytes from the parsed fields. */
    function canonical(seal) {
        return new TextEncoder().encode('timbrica-seal/2\n' + JSON.stringify({
            alg: seal.alg, hash: seal.hash, signer: seal.signer || '', sealedAt: seal.sealedAt || '',
            name: seal.name || '', kind: seal.kind || ''
        }));
    }

    /* ── key handling ────────────────────────────────────────────── */
    function generateKeyPair() {
        return subtle().generateKey(
            { name: 'ECDSA', namedCurve: 'P-256' },
            true,
            ['sign', 'verify']
        );
    }
    function exportJwk(key) { return subtle().exportKey('jwk', key); }
    function importPrivateJwk(jwk) {
        return subtle().importKey('jwk', jwk, { name: 'ECDSA', namedCurve: 'P-256' }, true, ['sign']);
    }
    function importPublicJwk(jwk) {
        return subtle().importKey('jwk', jwk, { name: 'ECDSA', namedCurve: 'P-256' }, true, ['verify']);
    }

    /*
     * createSeal(pdfBytes, opts) -> Promise<{ seal, code, keyExport }>
     *   opts.signerName  — self-declared name (string)
     *   opts.keyPairJwk  — optional { publicJwk, privateJwk } to reuse an
     *                      existing identity across documents
     *
     * `seal` is the JSON object, `code` is its base64 form (paste-able),
     * `keyExport` is { publicJwk, privateJwk } so the caller can offer
     * "save my key" for reuse.
     */
    function createSeal(pdfBytes, opts) {
        opts = opts || {};
        // pdfBytes may be null when the caller supplies opts.precomputedHash — the
        // SHA-256 of the document, computed incrementally for very large files so
        // they never have to live in one ArrayBuffer (see hash-stream.js).
        var bytes = pdfBytes instanceof Uint8Array ? pdfBytes : (pdfBytes ? new Uint8Array(pdfBytes) : new Uint8Array(0));
        var hashPromise = (opts.precomputedHash instanceof Uint8Array && opts.precomputedHash.length === 32)
            ? Promise.resolve(opts.precomputedHash) : sha256(bytes);
        var keyPromise, privExport, pubExport;

        if (opts.keyPairJwk && opts.keyPairJwk.privateJwk) {
            keyPromise = Promise.all([
                importPrivateJwk(opts.keyPairJwk.privateJwk),
                Promise.resolve(opts.keyPairJwk.privateJwk),
                Promise.resolve(opts.keyPairJwk.publicJwk)
            ]).then(function (r) {
                return { priv: r[0], privJwk: r[1], pubJwk: r[2] };
            });
        } else {
            keyPromise = generateKeyPair().then(function (kp) {
                return Promise.all([exportJwk(kp.privateKey), exportJwk(kp.publicKey)]).then(function (j) {
                    return { priv: kp.privateKey, privJwk: j[0], pubJwk: j[1] };
                });
            });
        }

        return Promise.all([hashPromise, keyPromise]).then(function (r) {
            var hash = r[0], keys = r[1];
            privExport = keys.privJwk;
            pubExport = keys.pubJwk;
            var seal = {
                v: SEAL_VERSION,
                alg: ALG,
                signer: String(opts.signerName || '').slice(0, 120),
                sealedAt: new Date().toISOString(),
                hash: bytesToB64(hash)
            };
            if (opts.name) seal.name = String(opts.name).slice(0, 160);
            if (opts.kind) seal.kind = String(opts.kind).slice(0, 16);
            return subtle().sign({ name: 'ECDSA', hash: 'SHA-256' }, keys.priv, canonical(seal)).then(function (sig) {
                seal.sig = bytesToB64(new Uint8Array(sig));
                seal.pub = pubExport;
                return {
                    seal: seal,
                    code: strToB64(JSON.stringify(seal)),
                    keyExport: { publicJwk: pubExport, privateJwk: privExport }
                };
            });
        });
    }

    /* Parse a seal from either a JSON object, a JSON string, or a base64 code. */
    function parseSeal(input) {
        if (input && typeof input === 'object') return input;
        var str = String(input || '').trim();
        if (!str) throw new Error('empty_seal');
        if (str.charAt(0) === '{') return JSON.parse(str);
        return JSON.parse(b64ToStr(str));   // base64 code
    }

    /*
     * verify(pdfBytes, sealInput) -> Promise<{ outcome, signer, sealedAt }>
     *   outcome ∈ 'valid' | 'modified' | 'bad_seal'
     *     valid    — file unchanged since sealing, signature checks out
     *     modified — file hash differs from the seal → the PDF was altered
     *     bad_seal — seal is malformed, or the signature does not verify
     */
    function verify(pdfBytes, sealInput, opts) {
        opts = opts || {};
        var bytes = pdfBytes instanceof Uint8Array ? pdfBytes : (pdfBytes ? new Uint8Array(pdfBytes) : new Uint8Array(0));
        var seal;
        try {
            seal = parseSeal(sealInput);
        } catch (e) {
            return Promise.resolve({ outcome: 'bad_seal', reason: 'parse_failed' });
        }
        if (!seal || seal.alg !== ALG || !seal.hash || !seal.sig || !seal.pub) {
            return Promise.resolve({ outcome: 'bad_seal', reason: 'malformed' });
        }
        var v2 = (seal.v || 1) >= 2;
        var meta = { signer: seal.signer || '', sealedAt: seal.sealedAt || '', metaProtected: false, version: v2 ? 2 : 1 };

        var hashPromise = (opts.precomputedHash instanceof Uint8Array && opts.precomputedHash.length === 32)
            ? Promise.resolve(opts.precomputedHash) : sha256(bytes);
        return hashPromise.then(function (hash) {
            var hashB64 = bytesToB64(hash);
            if (hashB64 !== seal.hash) {
                return Object.assign({ outcome: 'modified', reason: 'hash_mismatch' }, meta);
            }
            return importPublicJwk(seal.pub).then(function (pubKey) {
                return subtle().verify(
                    { name: 'ECDSA', hash: 'SHA-256' },
                    pubKey,
                    b64ToBytes(seal.sig),
                    v2 ? canonical(seal) : hash
                );
            }).then(function (ok) {
                if (ok && v2) meta.metaProtected = true;
                return Object.assign({ outcome: ok ? 'valid' : 'bad_seal', reason: ok ? '' : 'sig_invalid' }, meta);
            }).catch(function () {
                return Object.assign({ outcome: 'bad_seal', reason: 'verify_error' }, meta);
            });
        });
    }

    root.PdfSeal = {
        VERSION: SEAL_VERSION,
        createSeal: createSeal,
        verify: verify,
        parseSeal: parseSeal,
        _canonical: canonical,
        _b64: { bytesToB64: bytesToB64, b64ToBytes: b64ToBytes, strToB64: strToB64, b64ToStr: b64ToStr }
    };

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = root.PdfSeal;
    }
})(typeof window !== 'undefined' ? window : globalThis);
